import { Link } from 'react-router-dom';
import { BLOG_ARTICLES } from '@/mocks/blog';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const CATEGORY_COLORS: Record<string, string> = {
  'Иммунитет': '#ff2d78',
  'Уход за кожей': '#ff6b00',
  'Витамины и БАД': '#00f5ff',
  'Здоровый сон': '#39ff14',
  'Простуда и грипп': '#f0ff00',
  'Сердце и сосуды': '#ff2d78',
  'Пищеварение': '#39ff14',
  'Детское здоровье': '#00f5ff',
};

const BlogCard = ({ article, visible, delay }: { article: typeof BLOG_ARTICLES[0]; visible: boolean; delay: number }) => {
  const color = CATEGORY_COLORS[article.category] ?? '#ff2d78';
  const dateStr = new Date(article.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

  return (
    <Link
      to={`/blog/${article.slug}`}
      className={`group flex flex-col rounded-2xl overflow-hidden transition-all duration-300 reveal-hidden ${visible ? 'reveal-up' : ''}`}
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${color}15`,
        animationDelay: `${delay}ms`,
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}35`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}15`; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
    >
      {/* Image */}
      <div className="relative h-40 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-transparent to-transparent" />
        <div className="absolute top-3 left-3">
          <span
            className="px-2.5 py-1 rounded-full font-mono text-[10px] font-bold"
            style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
          >
            {article.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-2 line-clamp-2 group-hover:opacity-80 transition-opacity">
          {article.title}
        </h3>
        <p className="font-mono text-xs text-white/35 line-clamp-2 flex-1 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-1.5">
            <i className="ri-time-line text-[10px]" style={{ color }} />
            <span className="font-mono text-[10px] text-white/40">{article.readTime} мин</span>
          </div>
          <span className="font-mono text-[10px] text-white/25">{dateStr}</span>
        </div>
      </div>
    </Link>
  );
};

const BlogSection = () => {
  const featuredArticles = BLOG_ARTICLES.slice(0, 4);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { containerRef: gridRef, visibleItems } = useStaggerReveal(4, { threshold: 0.06 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal({ threshold: 0.3 });

  return (
    <section className="px-6 lg:px-12 py-20" style={{ background: '#050508' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 reveal-hidden ${headerVisible ? 'reveal-up' : ''}`}
        >
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-[#00f5ff]/60 mb-2">ЭКСПЕРТНЫЕ МАТЕРИАЛЫ</p>
            <h2 className="font-orbitron font-black text-3xl md:text-4xl text-white">
              СТАТЬИ О<br />
              <span style={{ color: '#00f5ff', textShadow: '0 0 20px #00f5ff60' }}>ЗДОРОВЬЕ</span>
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <p className="font-mono text-xs text-white/30 max-w-xs hidden md:block">
              Полезные советы от врачей и экспертов по здоровью, питанию и профилактике заболеваний
            </p>
            <Link
              to="/blog"
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-300"
              style={{ border: '1px solid #00f5ff50', color: '#00f5ff' }}
            >
              Все статьи
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {featuredArticles.map((article, index) => (
            <BlogCard
              key={article.id}
              article={article}
              visible={visibleItems[index]}
              delay={index * 90}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`mt-10 flex flex-wrap items-center justify-center gap-4 reveal-hidden ${ctaVisible ? 'reveal-fade' : ''}`}
        >
          <span className="font-mono text-xs text-white/30">Популярные темы:</span>
          {['Иммунитет', 'Витамины', 'Сон', 'Питание'].map((tag) => (
            <Link
              key={tag}
              to={`/blog?category=${tag.toLowerCase()}`}
              className="px-3 py-1.5 rounded-full font-mono text-xs transition-all duration-200"
              style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.2)', color: '#00f5ff' }}
            >
              {tag}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
